import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import '../App.css'
import backImg from '../images/back.png'

import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";

interface Student {
  name: string;
  dob: string;
}

type AddDataProps = {
  setPage: (page: string) => void;
  notify: () => void;
  setNotificationMessage:  (page: string) => void;
};

const AddData: React.FC<AddDataProps> = ({ setPage,notify,setNotificationMessage }) => {
  const [registry, setRegistry] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [address1, setAddress1] = useState<string>("");
  const [address2, setAddress2] = useState<string>("");
  const [address3, setAddress3] = useState<string>("");
  const [postcode, setPostcode] = useState<string>("");
  const [studentsCount, setStudentsCount] = useState<number>(1);
  const [students, setStudents] = useState<Student[]>([
    { name: "", dob: "" },
  ]);

  const registryRef = useRef<HTMLSelectElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const address1Ref = useRef<HTMLInputElement>(null);
  const address3Ref = useRef<HTMLInputElement>(null);
  const postcodeRef = useRef<HTMLInputElement>(null);
  const studentRefs = useRef<{ name: HTMLInputElement | null; dob: HTMLInputElement | null }[]>([]);
  const fpInstances = useRef<any[]>([]);

  useEffect(() => {
    setStudents((prev) => {
      const updated = [...prev];
      if (studentsCount > prev.length) {
        for (let i = prev.length; i < studentsCount; i++) {
          updated.push({ name: "", dob: "" });
        }
      } else if (studentsCount < prev.length) {
        updated.length = studentsCount;
      }
      return updated;
    });
  }, [studentsCount]);

  const handleBack = () => {
    setPage("builder");
  };

  const handleSave = async () => {
    let hasError = false;

    const checkField = (ref: React.RefObject<HTMLInputElement | HTMLSelectElement | null>) => {
      if (ref.current) {
        if (!ref.current.value.trim()) {
          ref.current.classList.add("error");
          hasError = true;
        } else {
          ref.current.classList.remove("error");
        }
      }
    };

    checkField(registryRef);
    checkField(titleRef);
    checkField(firstNameRef);
    checkField(lastNameRef);
    checkField(address1Ref);
    checkField(address3Ref);
    checkField(postcodeRef);

    // Validate using component state (students) and mark the corresponding inputs
    students.forEach((s, i) => {
      const nameEl = studentRefs.current[i]?.name;
      const dobEl = studentRefs.current[i]?.dob;

      if (!s.name || !s.name.trim()) {
        if (nameEl) nameEl.classList.add("error");
        hasError = true;
      } else {
        if (nameEl) nameEl.classList.remove("error");
      }

      if (!s.dob || !s.dob.trim()) {
        if (dobEl) dobEl.classList.add("error");
        hasError = true;
      } else {
        if (dobEl) dobEl.classList.remove("error");
      }
    });

    if (hasError) {
      setNotificationMessage("Please fill all required fields!");
      notify();
      return;
    }

    const response = await fetch("https://lscinvoice.com/api/add-customer/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        registry,
        title,
        firstName,
        lastName,
        address1,
        address2,
        address3,
        postcode,
        students,
      }),
    });

    const data = await response.json();
    console.log("Server response:", data);
    if (data.message === "OK") {
      setNotificationMessage("Added Customer Successfully!");
      notify();
      setPage("builder");
    } else {
      setNotificationMessage("Error Adding Customer!");
      notify();
    }
  };

  const handleStudentChange = (
    index: number,
    field: keyof Student,
    value: string
  ) => {
    setStudents(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };


  useEffect(() => {
    // Sync flatpickr instances with the students state (set selected date silently)
    students.forEach((student, index) => {
      const inst = fpInstances.current[index];
      if (inst) {
        if (student.dob) inst.setDate(student.dob, false, "Y-m-d");
      }
    });
  }, [students]);

  // Ensure fpInstances array doesn't leak instances when students are reduced
  useEffect(() => {
    if (fpInstances.current.length > students.length) {
      for (let i = students.length; i < fpInstances.current.length; i++) {
        try { fpInstances.current[i]?.destroy(); } catch (e) {}
        fpInstances.current[i] = undefined;
      }
      fpInstances.current.length = students.length;
    }
  }, [students.length]);

  // Cleanup flatpickr instances on unmount
  useEffect(() => {
    return () => {
      fpInstances.current.forEach((inst) => inst?.destroy && inst.destroy());
      fpInstances.current = [];
    };
  }, []);


  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="centered bg-white">
      <button
        className="flex customer-button mb-5"
        onClick={handleBack}
      >
        <img className="icons" src={backImg} />
      </button>

      <div className="flex">
        <label>Registry: <b className="required-font">*</b></label>
        <select
          ref={registryRef}
          className="align-right"
          value={registry}
          onChange={(e) => setRegistry(e.target.value)}
        >
          <option disabled value="">Registry</option>
          <option value="EY557846">EY557846</option>
          <option value="2649415">2649415</option>
        </select>
      </div>

      <h3>Parent Details</h3>

      <div className="flex">
        <label>Title: <b className="required-font">*</b></label>
        <input
          ref={titleRef}
          className="align-right data-input"
          type="text"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
      </div>

      <div className="flex">
        <label>First Name: <b className="required-font">*</b></label>
        <input
          ref={firstNameRef}
          className="align-right data-input"
          type="text"
          placeholder="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
        />
      </div>

      <div className="flex">
        <label>Last Name: <b className="required-font">*</b></label>
        <input
          ref={lastNameRef}
          className="align-right data-input"
          type="text"
          placeholder="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
        />
      </div>

      <div className="flex">
        <label>Address: <b className="required-font">*</b></label>
        <input
          ref={address1Ref}
          className="align-right data-input"
          type="text"
          placeholder="Address"
          value={address1}
          onChange={(e) => setAddress1(e.target.value)}
          required
        />
      </div>

      <div className="flex">
        <label>Town: </label>
        <input
          className="align-right data-input"
          type="text"
          placeholder="Town"
          value={address2}
          onChange={(e) => setAddress2(e.target.value)}
        />
      </div>

      <div className="flex">
        <label>County: <b className="required-font">*</b></label>
        <input
          ref={address3Ref}
          className="align-right data-input"
          type="text"
          placeholder="County"
          value={address3}
          onChange={(e) => setAddress3(e.target.value)}
          required
        />
      </div>

      <div className="flex">
        <label>Postcode: <b className="required-font">*</b></label>
        <input
          ref={postcodeRef}
          className="align-right data-input"
          type="text"
          placeholder="Postcode"
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          required
        />
      </div>

      <div className="flex">
        <label>No of Students:</label>
        <input
          className="align-right data-input"
          type="number"
          min={1}
          max={20}
          value={studentsCount}
          onChange={(e) => setStudentsCount(Math.max(Number(e.target.value),1))}
        />
      </div>

      <h3>Student Details</h3>
      {students.map((student, index) => (
        <React.Fragment key={index}>
          <div className="flex">
            <label>Student {index + 1} Name: <b className="required-font">*</b></label>
            <input
              ref={(el) => {
                if (!studentRefs.current[index]) studentRefs.current[index] = { name: null, dob: null };
                studentRefs.current[index].name = el;
              }}
              className="align-right data-input"
              type="text"
              placeholder="student name"
              value={student.name}
              required
              onChange={(e) =>
                handleStudentChange(index, "name", e.target.value)
              }
            />
          </div>

          <div className="flex">
            <label>Student {index + 1} DOB: <b className="required-font">*</b></label>
            <input
              ref={(el) => {
                if (!studentRefs.current[index]) studentRefs.current[index] = { name: null, dob: null };
                studentRefs.current[index].dob = el;

                if (el) {
                  // destroy existing instance at this index (if any)
                  if (fpInstances.current[index]) {
                    try { fpInstances.current[index].destroy(); } catch (e) {}
                  }

                  // initialize flatpickr: keep raw value as ISO (Y-m-d) and display alt format
                  fpInstances.current[index] = flatpickr(el, {
                    dateFormat: "Y-m-d",
                    altInput: true,
                    altFormat: "d/m/Y",
                    allowInput: true,
                    defaultDate: students[index]?.dob || undefined,
                    onChange: (selectedDates) => {
                      if (selectedDates.length > 0) {
                        const iso = selectedDates[0].toISOString().split("T")[0];
                        handleStudentChange(index, "dob", iso);
                      } else {
                        handleStudentChange(index, "dob", "");
                      }
                    },
                  });
                } else {
                  // element unmounted -> destroy instance
                  if (fpInstances.current[index]) {
                    try { fpInstances.current[index].destroy(); } catch (e) {}
                    fpInstances.current[index] = undefined;
                  }
                }
              }}
              className="align-right data-input"
              type="text"
              placeholder="dd/mm/yyyy"
              required
            />
          </div>
        </React.Fragment>
      ))}

      <div className="flex mt-5 save-button">
        <motion.button
          initial={{ backgroundImage: "linear-gradient(to top, rgba(0, 110, 0, 0), white, white)" }}
          whileHover={{ backgroundImage: "linear-gradient(to top, rgba(0, 110, 0, 0.3), white, white)" }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className="align-right align-left green-button "
          onClick={handleSave}
        >
          Save
        </motion.button>
      </div>
    </motion.div>
  );
};

export default AddData;
